// ASSIGNMENTS_FLOATING_HORIZONTAL_SCROLL_WORKING_V3
// ASSIGNMENTS_FLOATING_HORIZONTAL_SCROLL_V2
// ASSIGNMENTS_TOP_HORIZONTAL_SCROLL_V1
// ASSIGNMENTS_SINGLE_SCROLL_WORKSPACE_V1
import { getEffectiveToday } from '../../../utils/systemTime';
import { addDaysToDateString } from '../../../utils/assignmentTiming';
import {
  calculateAttendanceContribution,
  normalizeAttendanceSettings
} from '../../../utils/attendanceCalculations';
import {
  buildProgressAssessmentUnits,
  collectProgressSubmissionEnrolmentIds,
  fetchProgressCqManualMarks,
  fetchProgressSubmissionData,
  latestProgressFeedback,
  loadProgressAssignmentContext,
  summariseProgressUnits
} from '../../../utils/studentProgressAssessmentUnits';
import {
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';

import { isProgressSubmissionComplete } from '../../../utils/studentProgressAssessmentUnits';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const AUTO_REFRESH_MS = 30000;

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchEnrolments(
  courseId,
  includeMaster = false
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=*,students(id,student_code,english_name,chinese_name,is_master)&order=created_at.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  const data = await response.json();

  return (Array.isArray(data) ? data : [])
    .filter(
      enrolment =>
        includeMaster ||
        enrolment.students?.is_master !== true
    );
}

async function fetchAttendance(ids) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance?enrolment_id=in.(${ids})&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAttendanceGroupContext(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [membershipsResponse, weeksResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_memberships?select=enrolment_id,attendance_group_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_weeks?select=attendance_group_id,course_week_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
      { headers }
    )
  ]);

  const memberships = await membershipsResponse.json();
  const groupWeeks = await weeksResponse.json();

  return {
    memberships: Array.isArray(memberships) ? memberships : [],
    groupWeeks: Array.isArray(groupWeeks) ? groupWeeks : []
  };
}

async function fetchCqSubmissions(ids) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=in.(${ids})&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchPdSubmissions(ids) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions?enrolment_id=in.(${ids})&select=*,pd_feedback(*)`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAssignmentGroupGradeOverrides(
  enrolmentIds
) {
  const ids =
    (Array.isArray(enrolmentIds)
      ? enrolmentIds
      : []
    )
      .filter(Boolean);

  if (!ids.length) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_group_grade_overrides' +
        '?enrolment_id=in.(' +
        ids.join(',') +
        ')' +
        '&select=assignment_submission_id,enrolment_id,score',
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading individual group-grade overrides failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return Array.isArray(rows)
    ? rows
    : [];
}


async function fetchTraditionalProgressConfig(
  courseId
) {
  const headers = {
    'apikey':
      SUPABASE_KEY,

    'Authorization':
      'Bearer ' +
      SUPABASE_KEY
  };

  const [
    typeResponse,
    weightResponse
  ] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/assignment_types?select=id,name`,
        { headers }
      ),

      fetch(
        `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&select=assignment_type_id,weight_percent`,
        { headers }
      )
    ]);

  const [
    types,
    weights
  ] =
    await Promise.all([
      typeResponse.json(),
      weightResponse.json()
    ]);

  return {
    types:
      Array.isArray(types)
        ? types
        : [],

    weights:
      Array.isArray(weights)
        ? weights
        : []
  };
}

// STUDENT_PROGRESS_ASSIGNMENT_ORDER_20260919
function orderProgressAssignments(
  assignments,
  typeSettings
) {
  const rows =
    Array.isArray(assignments)
      ? assignments
      : [];

  const menuOrderByType =
    new Map(
      (
        Array.isArray(typeSettings)
          ? typeSettings
          : []
      ).map(setting => {
        const rawOrder =
          setting?.menu_order;

        const numericOrder =
          rawOrder === null ||
          rawOrder === undefined ||
          rawOrder === ''
            ? null
            : Number(rawOrder);

        return [
          String(
            setting.assignment_type_id
          ),
          Number.isFinite(
            numericOrder
          )
            ? numericOrder
            : null
        ];
      })
    );

  const firstTypeIndex =
    new Map();

  rows.forEach(
    (assignment, index) => {
      const typeId =
        String(
          assignment
            .assignment_type_id ||
          ''
        );

      if (
        !firstTypeIndex.has(
          typeId
        )
      ) {
        firstTypeIndex.set(
          typeId,
          index
        );
      }
    }
  );


  return rows
    .slice()
    .sort(
      (a, b) => {
        const aType =
          String(
            a.assignment_type_id ||
            ''
          );

        const bType =
          String(
            b.assignment_type_id ||
            ''
          );


        // Keep every instance of the
        // same assignment type together.
        if (aType === bType) {
          return (
            (
              Number(
                a.instance_number
              ) || 0
            ) -
              (
                Number(
                  b.instance_number
                ) || 0
              ) ||
            String(a.id)
              .localeCompare(
                String(b.id)
              )
          );
        }


        const aOrder =
          menuOrderByType.get(
            aType
          );

        const bOrder =
          menuOrderByType.get(
            bType
          );

        const aHasOrder =
          Number.isFinite(
            aOrder
          );

        const bHasOrder =
          Number.isFinite(
            bOrder
          );


        if (
          aHasOrder &&
          bHasOrder
        ) {
          return (
            aOrder -
              bOrder ||
            (
              firstTypeIndex.get(
                aType
              ) || 0
            ) -
              (
                firstTypeIndex.get(
                  bType
                ) || 0
              )
          );
        }

        if (aHasOrder) {
          return -1;
        }

        if (bHasOrder) {
          return 1;
        }

        return (
          (
            firstTypeIndex.get(
              aType
            ) || 0
          ) -
          (
            firstTypeIndex.get(
              bType
            ) || 0
          )
        );
      }
    );
}

async function fetchSelfEvalResponses(ids) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_responses?enrolment_id=in.(${ids})&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchQiData(weekIds) {
  if (!weekIds) return { assignments: [], submissions: [] };

  const [assignmentResponse, submissionResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_assignments?course_week_id=in.(${weekIds})&select=*`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/cq_question_improvements?select=*,cq_question_assignments!inner(course_week_id)&cq_question_assignments.course_week_id=in.(${weekIds})`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    )
  ]);

  const [assignments, submissions] = await Promise.all([
    assignmentResponse.json(),
    submissionResponse.json()
  ]);

  return {
    assignments: Array.isArray(assignments) ? assignments : [],
    submissions: Array.isArray(submissions) ? submissions : []
  };
}

function qiSubmissionIncludesEnrolment(
  submission,
  enrolmentId
) {
  const targetId =
    String(enrolmentId);

  if (
    String(
      submission?.enrolment_id ||
      ''
    ) === targetId
  ) {
    return true;
  }

  return (
    Array.isArray(
      submission?.group_member_ids
    ) &&
    submission.group_member_ids.some(
      id =>
        String(id) ===
        targetId
    )
  );
}


async function fetchCqSubmissionsByEnrolment(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=eq.${enrolmentId}&select=*,course_weeks(week_number,class_date)&order=submitted_at.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

function getToday() {
  return getEffectiveToday();
}

function getWeeksToDate(weeks) {
  const today = getToday();
  return weeks.filter(w => w.class_date <= today);
}


function getProgressPdDueDate(
  course,
  week,
  submission = null
) {
  if (!week?.class_date) {
    return null;
  }

  let baseDueDate =
    String(
      week.class_date
    ).slice(0, 10);

  if (course?.pd_due_day) {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];

    const target =
      days.indexOf(
        course.pd_due_day
      );

    if (target >= 0) {
      const parts =
        baseDueDate
          .split('-')
          .map(Number);

      const date =
        new Date(
          parts[0],
          parts[1] - 1,
          parts[2],
          12,
          0,
          0
        );

      while (
        date.getDay() !==
        target
      ) {
        date.setDate(
          date.getDate() + 1
        );
      }

      baseDueDate = [
        date.getFullYear(),
        String(
          date.getMonth() + 1
        ).padStart(2, '0'),
        String(
          date.getDate()
        ).padStart(2, '0')
      ].join('-');
    }
  }

  return addDaysToDateString(
    baseDueDate,
    Math.max(
      0,
      Number(
        submission
          ?.extension_days
      ) || 0
    )
  );
}

function getStudentName(student) {
  const names = [
    student?.english_name,
    student?.chinese_name
  ].filter(Boolean);

  return (
    names.join(' / ') ||
    student?.student_code ||
    '—'
  );
}

function getStudentIdentity(student) {
  return [
    student?.student_code,
    student?.english_name,
    student?.chinese_name
  ]
    .filter(Boolean)
    .join(' — ') || '—';
}

function StatPill({ value, max, color }) {
  return (
    <span style={{
      ...styles.pill,
      background: color === 'blue' ? '#e7f1f7' : color === 'green' ? '#e7f5ed' : color === 'red' ? '#fde8e8' : '#f0f4f7',
      color: color === 'blue' ? '#215b7d' : color === 'green' ? '#216e45' : color === 'red' ? '#a12a2a' : '#66788a'
    }}>
      {value}{max !== undefined ? `/${max}` : ''}
    </span>
  );
}

function useTopHorizontalScrollbar(refreshKey) {
  const contentScrollRef =
    useRef(null);

  const topScrollRef =
    useRef(null);

  const [
    scrollMetrics,
    setScrollMetrics
  ] =
    useState({
      scrollWidth: 0,
      clientWidth: 0
    });

  const [
    floatingBar,
    setFloatingBar
  ] =
    useState({
      visible: false,
      left: 0,
      width: 0,
      bottom: 0
    });

  const updateFloatingBar =
    useCallback(() => {
      const scroller =
        contentScrollRef.current;

      if (
        !scroller ||
        typeof window === 'undefined'
      ) {
        setFloatingBar(
          previous =>
            previous.visible
              ? {
                  ...previous,
                  visible: false
                }
              : previous
        );

        return;
      }

      const rect =
        scroller
          .getBoundingClientRect();

      const viewportWidth =
        window.innerWidth ||
        document.documentElement
          .clientWidth ||
        0;

      const viewportHeight =
        window.innerHeight ||
        document.documentElement
          .clientHeight ||
        0;

      const hasOverflow =
        scroller.scrollWidth >
          scroller.clientWidth +
            1;

      const left =
        Math.max(
          0,
          Math.round(
            rect.left
          )
        );

      const right =
        Math.min(
          viewportWidth,
          Math.round(
            rect.right
          )
        );

      const width =
        Math.max(
          0,
          right - left
        );

      const visible =
        hasOverflow &&
        width > 0 &&
        rect.bottom > 0 &&
        rect.top <
          viewportHeight;

      /*
       * While the table extends below
       * the viewport, sit at the bottom
       * of the browser window.
       *
       * Once the real bottom of the
       * table comes into view, rise with
       * it instead of floating over the
       * content beneath the table.
       */
      const bottom =
        visible
          ? Math.max(
              0,
              Math.round(
                viewportHeight -
                  rect.bottom
              )
            )
          : 0;

      setFloatingBar(
        previous => {
          if (
            previous.visible ===
              visible &&
            previous.left ===
              left &&
            previous.width ===
              width &&
            previous.bottom ===
              bottom
          ) {
            return previous;
          }

          return {
            visible,
            left,
            width,
            bottom
          };
        }
      );

    }, []);

  const measure =
    useCallback(() => {
      const scroller =
        contentScrollRef.current;

      if (!scroller) {
        setScrollMetrics({
          scrollWidth: 0,
          clientWidth: 0
        });

        updateFloatingBar();
        return;
      }

      const next = {
        scrollWidth:
          Math.ceil(
            scroller.scrollWidth ||
            0
          ),

        clientWidth:
          Math.ceil(
            scroller.clientWidth ||
            0
          )
      };

      setScrollMetrics(
        previous =>
          previous.scrollWidth ===
              next.scrollWidth &&
          previous.clientWidth ===
              next.clientWidth
            ? previous
            : next
      );

      updateFloatingBar();

      if (
        topScrollRef.current
      ) {
        topScrollRef
          .current
          .scrollLeft =
            scroller.scrollLeft;
      }
    }, [
      updateFloatingBar
    ]);

  useEffect(() => {
    measure();

    const scroller =
      contentScrollRef.current;

    const content =
      scroller
        ?.firstElementChild ||
      null;

    const observer =
      typeof ResizeObserver !==
        'undefined'
        ? new ResizeObserver(
            measure
          )
        : null;

    if (scroller) {
      observer?.observe(
        scroller
      );
    }

    if (content) {
      observer?.observe(
        content
      );
    }

    window.addEventListener(
      'resize',
      measure
    );

    window.addEventListener(
      'scroll',
      updateFloatingBar,
      true
    );

    return () => {
      observer?.disconnect();

      window.removeEventListener(
        'resize',
        measure
      );

      window.removeEventListener(
        'scroll',
        updateFloatingBar,
        true
      );
    };
  }, [
    measure,
    refreshKey,
    updateFloatingBar
  ]);

  const onTopScroll =
    useCallback(
      event => {
        const scroller =
          contentScrollRef.current;

        if (!scroller) {
          return;
        }

        const next =
          event.currentTarget
            .scrollLeft;

        if (
          Math.abs(
            scroller.scrollLeft -
              next
          ) > 0.5
        ) {
          scroller.scrollLeft =
            next;
        }
      },
      []
    );

  const onContentScroll =
    useCallback(
      event => {
        const topScroller =
          topScrollRef.current;

        if (!topScroller) {
          return;
        }

        const next =
          event.currentTarget
            .scrollLeft;

        if (
          Math.abs(
            topScroller.scrollLeft -
              next
          ) > 0.5
        ) {
          topScroller.scrollLeft =
            next;
        }
      },
      []
    );

  return {
    contentScrollRef,
    topScrollRef,
    onTopScroll,
    onContentScroll,

    scrollWidth:
      scrollMetrics.scrollWidth,

    needsScroll:
      scrollMetrics.scrollWidth >
        scrollMetrics.clientWidth +
          1,

    floatingVisible:
      floatingBar.visible,

    floatingLeft:
      floatingBar.left,

    floatingWidth:
      floatingBar.width,

    floatingBottom:
      floatingBar.bottom
  };
}


export default function StudentProgress({ course, session, embeddedEnrolmentId = null }) {
  const [loading, setLoading] = useState(true);
  const [refreshError, setRefreshError] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [attendanceGroupMemberships, setAttendanceGroupMemberships] =
    useState([]);
  const [attendanceGroupWeeks, setAttendanceGroupWeeks] = useState([]);
  const [cqSubmissions, setCqSubmissions] = useState([]);

  const [
    cqManualSubmissionMarks,
    setCqManualSubmissionMarks
  ] = useState([]);

  const [pdSubmissions, setPdSubmissions] = useState([]);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);

  const [
    assignmentGradeOverrides,
    setAssignmentGradeOverrides
  ] = useState([]);

  const [assignmentCompletions, setAssignmentCompletions] = useState([]);

  const [
    traditionalAssignmentTypes,
    setTraditionalAssignmentTypes
  ] = useState([]);

  const [
    traditionalAssignmentWeights,
    setTraditionalAssignmentWeights
  ] = useState([]);

  const [
    progressAssignmentContext,
    setProgressAssignmentContext
  ] = useState(null);

  const [selfEvalResponses, setSelfEvalResponses] = useState([]);
  const [qiAssignments, setQiAssignments] = useState([]);
  const [qiSubmissions, setQiSubmissions] = useState([]);
  const [viewStudent, setViewStudent] = useState(null);

  const [viewCqSubs, setViewCqSubs] = useState([]);
  const [viewCqLoading, setViewCqLoading] = useState(false);

  const refreshInFlight =
    useRef(false);

  const hasLoadedOnce =
    useRef(false);

  useEffect(() => {
    if (
      !viewStudent ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      'hidden';

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [viewStudent]);


  const refreshData =
    useCallback(
      async ({
        showLoading = false
      } = {}) => {
        if (
          refreshInFlight
            .current
        ) {
          return;
        }

        refreshInFlight
          .current =
          true;

        if (
          showLoading &&
          !hasLoadedOnce
            .current
        ) {
          setLoading(true);
        }

        try {
          const [
            newWeeks,
            newEnrolments
          ] =
            await Promise.all([
              fetchWeeks(
                course.id
              ),

              fetchEnrolments(
                course.id,
                session?.isSuperadmin === true
              )
            ]);

          const enrolmentIds =
            newEnrolments
              .map(
                enrolment =>
                  enrolment.id
              )
              .filter(Boolean);

          const enrolmentFilter =
            enrolmentIds
              .join(',');

          const progressContext =
            await loadProgressAssignmentContext({
              courseId:
                course.id,

              enrolments:
                newEnrolments
            });

          const submissionEnrolmentIds =
            collectProgressSubmissionEnrolmentIds({
              enrolments:
                newEnrolments,

              context:
                progressContext
            });

          const [
            newAttendance,
            newCq,
            newCqManual,
            newPd,
            progressSubmissionData,
            newGradeOverrides,
            traditionalConfig,
            newSelfEvals,
            attendanceGroupContext
          ] =
            await Promise.all([
              course
                .attendance_enabled ===
                false ||
              !enrolmentFilter
                ? Promise.resolve([])
                : fetchAttendance(
                    enrolmentFilter
                  ),

              course
                .cq_enabled ===
                false ||
              !enrolmentFilter
                ? Promise.resolve([])
                : fetchCqSubmissions(
                    enrolmentFilter
                  ),

              course
                .cq_enabled ===
                false
                ? Promise.resolve([])
                : fetchProgressCqManualMarks(
                    enrolmentIds
                  ),

              course
                .pd_enabled ===
                false ||
              !enrolmentFilter
                ? Promise.resolve([])
                : fetchPdSubmissions(
                    enrolmentFilter
                  ),

              fetchProgressSubmissionData(
                submissionEnrolmentIds
              ),

              fetchAssignmentGroupGradeOverrides(
                enrolmentIds
              ),

              course
                .grading_mode ===
                'traditional'
                ? fetchTraditionalProgressConfig(
                    course.id
                  )
                : Promise.resolve({
                    types: [],
                    weights: []
                  }),

              course
                .self_eval_enabled ===
                true &&
              enrolmentFilter
                ? fetchSelfEvalResponses(
                    enrolmentFilter
                  )
                : Promise.resolve([]),

              course
                .group_based_attendance
                ? fetchAttendanceGroupContext(
                    course.id
                  )
                : Promise.resolve({
                    memberships: [],
                    groupWeeks: []
                  })
            ]);

          let newQiAssignments =
            [];

          let newQiSubmissions =
            [];

          if (
            course
              .qi_enabled ===
            true
          ) {
            const qiWeekIds =
              newWeeks
                .filter(
                  week =>
                    week
                      .cq_enabled
                )
                .map(
                  week =>
                    week.id
                )
                .join(',');

            if (qiWeekIds) {
              const qi =
                await fetchQiData(
                  qiWeekIds
                );

              newQiAssignments =
                qi.assignments;

              newQiSubmissions =
                qi.submissions;
            }
          }

          setWeeks(
            newWeeks
          );

          setEnrolments(
            newEnrolments
          );

          setAttendance(
            Array.isArray(
              newAttendance
            )
              ? newAttendance
              : []
          );

          setAttendanceGroupMemberships(
            attendanceGroupContext
              ?.memberships ||
            []
          );

          setAttendanceGroupWeeks(
            attendanceGroupContext
              ?.groupWeeks ||
            []
          );

          setCqSubmissions(
            Array.isArray(newCq)
              ? newCq
              : []
          );

          setCqManualSubmissionMarks(
            Array.isArray(
              newCqManual
            )
              ? newCqManual
              : []
          );

          setPdSubmissions(
            Array.isArray(newPd)
              ? newPd
              : []
          );

          setCourseAssignments(
            orderProgressAssignments(
              progressContext
                .assignments,
              progressContext
                .assignmentTypeSettings
            )
          );

          setAssignmentSubmissions(
            progressSubmissionData
              .submissions ||
            []
          );

          setAssignmentGradeOverrides(
            Array.isArray(
              newGradeOverrides
            )
              ? newGradeOverrides
              : []
          );

          setAssignmentCompletions(
            progressSubmissionData
              .completions ||
            []
          );

          setProgressAssignmentContext(
            progressContext
          );

          setTraditionalAssignmentTypes(
            traditionalConfig
              ?.types ||
            []
          );

          setTraditionalAssignmentWeights(
            traditionalConfig
              ?.weights ||
            []
          );

          setSelfEvalResponses(
            Array.isArray(
              newSelfEvals
            )
              ? newSelfEvals
              : []
          );

          setQiAssignments(
            newQiAssignments
          );

          setQiSubmissions(
            newQiSubmissions
          );

          setRefreshError('');

          hasLoadedOnce
            .current =
            true;

        } catch (error) {
          console.error(
            'Loading Student Progress failed:',
            error
          );

          setRefreshError(
            error?.message ||
            'Could not refresh Student Progress.'
          );

        } finally {
          refreshInFlight
            .current =
            false;

          setLoading(false);
        }
      },
      [
        course.id,
        course.grading_mode,
        course.attendance_enabled,
        course.cq_enabled,
        course.pd_enabled,
        course.qi_enabled,
        course.group_based_attendance,
        course.self_eval_enabled,
        session?.isSuperadmin
      ]
    );


  useEffect(() => {
    hasLoadedOnce.current =
      false;

    refreshData({
      showLoading: true
    });
  }, [refreshData]);


  useEffect(() => {
    if (
      typeof window ===
      'undefined'
    ) {
      return undefined;
    }

    const intervalId =
      window.setInterval(
        () => {
          refreshData();
        },
        AUTO_REFRESH_MS
      );

    const handleFocus =
      () => {
        refreshData();
      };

    const handleVisibility =
      () => {
        if (
          document
            .visibilityState ===
          'visible'
        ) {
          refreshData();
        }
      };

    window.addEventListener(
      'focus',
      handleFocus
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      window.clearInterval(
        intervalId
      );

      window.removeEventListener(
        'focus',
        handleFocus
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [refreshData]);


  async function handleViewStudent(enrolment) {
    setViewStudent(
      enrolment
    );

    if (
      course
        .cq_enabled ===
      false
    ) {
      setViewCqSubs([]);
      setViewCqLoading(false);
      return;
    }

    setViewCqLoading(true);

    try {
      const cqData =
        await fetchCqSubmissionsByEnrolment(
          enrolment.id
        );

      setViewCqSubs(
        Array.isArray(cqData)
          ? cqData
          : []
      );
    } finally {
      setViewCqLoading(false);
    }
  }

  function getAttendanceWeeksForEnrolment(
    enrolment,
    sourceWeeks
  ) {
    if (
      course
        .attendance_enabled ===
      false
    ) {
      return [];
    }

    if (
      !course
        .group_based_attendance
    ) {
      return sourceWeeks
        .filter(
          week =>
            week
              .attendance_enabled
        );
    }

    const membership =
      attendanceGroupMemberships
        .find(
          row =>
            row
              .enrolment_id ===
            enrolment.id
        );

    if (!membership) {
      return [];
    }

    const requiredIds =
      new Set(
        attendanceGroupWeeks
          .filter(
            row =>
              row
                .attendance_group_id ===
              membership
                .attendance_group_id
          )
          .map(
            row =>
              row
                .course_week_id
          )
      );

    return sourceWeeks
      .filter(
        week =>
          requiredIds.has(
            week.id
          )
      );
  }

  function getStats(enrolment) {
    const today =
      getToday();

    const weeksToDate =
      getWeeksToDate(
        weeks
      );

    const attWeeks =
      getAttendanceWeeksForEnrolment(
        enrolment,
        weeksToDate
      );

    const cqWeeks =
      course
        .cq_enabled ===
        false
        ? []
        : weeksToDate
            .filter(
              week =>
                week
                  .cq_enabled
            );

    const pdEnabledWeeks =
      course
        .pd_enabled ===
        false
        ? []
        : weeks
            .filter(
              week =>
                week
                  .pd_enabled
            );

    const pdWeeks =
      pdEnabledWeeks
        .filter(
          week => {
            const submission =
              pdSubmissions
                .find(
                  row =>
                    row
                      .enrolment_id ===
                      enrolment.id &&
                    row
                      .week_id ===
                      week.id
                );

            const dueDate =
              getProgressPdDueDate(
                course,
                week,
                submission
              );

            return (
              !!dueDate &&
              dueDate <= today
            );
          }
        );

    const attPresent =
      new Set(
        attendance
          .filter(
            row =>
              row
                .enrolment_id ===
                enrolment.id &&
              row.present &&
              attWeeks
                .some(
                  week =>
                    week.id ===
                    row.week_id
                )
          )
          .map(
            row =>
              row.week_id
          )
      ).size;

    const cqWeekIds =
      new Set(
        cqWeeks.map(
          week =>
            week.id
        )
      );

    const completedCqWeekIds =
      new Set([
        ...cqSubmissions
          .filter(
            row =>
              row
                .enrolment_id ===
                enrolment.id &&
              cqWeekIds.has(
                row.week_id
              )
          )
          .map(
            row =>
              row.week_id
          ),

        ...cqManualSubmissionMarks
          .filter(
            row =>
              String(
                row.enrolment_id
              ) ===
                String(
                  enrolment.id
                ) &&
              cqWeekIds.has(
                row.week_id
              )
          )
          .map(
            row =>
              row.week_id
          )
      ]);

    const pdSubs =
      pdSubmissions
        .filter(
          row =>
            row
              .enrolment_id ===
              enrolment.id &&
            row.submitted &&
            pdWeeks
              .some(
                week =>
                  week.id ===
                  row.week_id
              )
        );

    const pdCommented =
      pdSubs
        .filter(
          row =>
            row
              .pd_feedback
              ?.submitted
        )
        .length;

    const qiRelevantAssignments =
      course
        .qi_enabled ===
        true
        ? qiAssignments.filter(
            assignment =>
              cqWeeks.some(
                week =>
                  week.id ===
                  assignment
                    .course_week_id
              )
          )
        : [];

    const qiAssignmentById =
      new Map(
        qiRelevantAssignments.map(
          assignment => [
            String(
              assignment.id
            ),
            assignment
          ]
        )
      );

    /*
     * Start with weeks belonging to the
     * student's nominal QI group.
     */
    const qiAssignedWeekIds =
      new Set(
        qiRelevantAssignments
          .filter(
            assignment =>
              Number(
                assignment.stream
              ) ===
                Number(
                  enrolment.stream
                ) &&
              Number(
                assignment
                  .group_number
              ) ===
                Number(
                  enrolment
                    .group_number
                )
          )
          .map(
            assignment =>
              assignment
                .course_week_id
          )
      );

    /*
     * Actual participation can be on any
     * group submission for that week.
     */
    const qiParticipantSubmissions =
      qiSubmissions.filter(
        submission =>
          qiSubmissionIncludesEnrolment(
            submission,
            enrolment.id
          ) &&
          qiAssignmentById.has(
            String(
              submission
                .cq_question_assignment_id
            )
          )
      );

    /*
     * Cross-group participation still belongs
     * in this student's own QI progress.
     */
    qiParticipantSubmissions.forEach(
      submission => {
        const assignment =
          qiAssignmentById.get(
            String(
              submission
                .cq_question_assignment_id
            )
          );

        if (
          assignment
            ?.course_week_id
        ) {
          qiAssignedWeekIds.add(
            assignment
              .course_week_id
          );
        }
      }
    );

    const qiSubmittedWeekIds =
      new Set(
        qiParticipantSubmissions
          .filter(
            submission =>
              submission
                .submitted ===
                true
          )
          .map(
            submission =>
              qiAssignmentById
                .get(
                  String(
                    submission
                      .cq_question_assignment_id
                  )
                )
                ?.course_week_id
          )
          .filter(Boolean)
      );

    const selfEvalMid =
      course
        .self_eval_enabled ===
        true
        ? selfEvalResponses
            .find(
              row =>
                row
                  .enrolment_id ===
                  enrolment.id &&
                row.section ===
                  'Mid-Semester'
            )
        : null;

    const selfEvalFinal =
      course
        .self_eval_enabled ===
        true
        ? selfEvalResponses
            .find(
              row =>
                row
                  .enrolment_id ===
                  enrolment.id &&
                row.section ===
                  'Final'
            )
        : null;

    return {
      attPresent,
      attTotal:
        attWeeks.length,

      cqCount:
        completedCqWeekIds.size,
      cqTotal:
        cqWeeks.length,

      qiCount:
        qiSubmittedWeekIds.size,
      qiTotal:
        qiAssignedWeekIds.size,

      pdCount:
        pdSubs.filter(row => isProgressSubmissionComplete(row, row.pd_feedback)).length,
      pdCommented,
      pdTotal:
        pdWeeks.length,

      selfEvalMid,
      selfEvalFinal
    };
  }

  function getQiDetail(enrolment) {
    if (
      course.qi_enabled !== true ||
      course.cq_enabled === false
    ) {
      return [];
    }

    return getWeeksToDate(weeks)
      .filter(
        week =>
          week.cq_enabled
      )
      .map(
        week => {
          const weekAssignments =
            qiAssignments.filter(
              assignment =>
                assignment
                  .course_week_id ===
                week.id
            );

          if (
            weekAssignments.length ===
            0
          ) {
            return null;
          }

          const assignmentIds =
            new Set(
              weekAssignments.map(
                assignment =>
                  String(
                    assignment.id
                  )
              )
            );

          const participantWork =
            qiSubmissions.filter(
              submission =>
                assignmentIds.has(
                  String(
                    submission
                      .cq_question_assignment_id
                  )
                ) &&
                qiSubmissionIncludesEnrolment(
                  submission,
                  enrolment.id
                )
            );

          const hasOwnGroupAssignment =
            weekAssignments.some(
              assignment =>
                Number(
                  assignment.stream
                ) ===
                  Number(
                    enrolment.stream
                  ) &&
                Number(
                  assignment
                    .group_number
                ) ===
                  Number(
                    enrolment
                      .group_number
                  )
            );

          if (
            !hasOwnGroupAssignment &&
            participantWork.length ===
              0
          ) {
            return null;
          }

          return {
            week,

            submitted:
              participantWork.some(
                submission =>
                  submission
                    .submitted ===
                    true
              )
          };
        }
      )
      .filter(Boolean);
  }

  function getAttendanceDetail(enrolment) {
    const weeksToDate = getAttendanceWeeksForEnrolment(
      enrolment,
      getWeeksToDate(weeks)
    );

    return weeksToDate.map(w => {
      const rec = attendance.find(a => a.enrolment_id === enrolment.id && a.week_id === w.id);
      return { week: w, present: rec?.present || false };
    });
  }

  function getPdDetail(enrolment) {
    if (
      course
        .pd_enabled ===
      false
    ) {
      return [];
    }

    return pdSubmissions
      .filter(
        row =>
          row.enrolment_id ===
            enrolment.id &&
          row.submitted
      )
      .map(
        row => {
          const week =
            weeks.find(
              candidate =>
                candidate.id ===
                row.week_id
            );

          return {
            ...row,
            week
          };
        }
      )
      .sort(
        (a, b) =>
          (
            a.week
              ?.week_number ||
            0
          ) -
          (
            b.week
              ?.week_number ||
            0
          )
      );
  }

  function formatTraditionalScore(
    value
  ) {
    const numeric =
      Number(value);

    if (
      !Number.isFinite(
        numeric
      )
    ) {
      return null;
    }

    return numeric
      .toFixed(1)
      .replace(/\.0$/, '');
  }

  function getTraditionalWeight(
    assignmentTypeId
  ) {
    const row =
      traditionalAssignmentWeights
        .find(
          weight =>
            String(
              weight
                .assignment_type_id
            ) ===
              String(
                assignmentTypeId
              )
        );

    const numeric =
      Number(
        row?.weight_percent
      );

    return Number.isFinite(
      numeric
    )
      ? numeric
      : 0;
  }

  function getTraditionalWeightByName(
    name
  ) {
    const type =
      traditionalAssignmentTypes
        .find(
          row =>
            row.name ===
            name
        );

    return type
      ? getTraditionalWeight(
          type.id
        )
      : 0;
  }

  function getSubmittedPdFeedback(
    submission
  ) {
    const value =
      submission
        ?.pd_feedback;

    const rows =
      Array.isArray(value)
        ? value
        : (
            value
              ? [value]
              : []
          );

    return rows
      .filter(
        feedback =>
          feedback
            ?.submitted ===
          true
      )
      .sort(
        (a, b) => {
          const aTime =
            new Date(
              a.submitted_at ||
              a.updated_at ||
              a.created_at ||
              0
            ).getTime();

          const bTime =
            new Date(
              b.submitted_at ||
              b.updated_at ||
              b.created_at ||
              0
            ).getTime();

          return bTime - aTime;
        }
      )[0] || null;
  }

  const progressUnitCache =
    new Map();


  function getProgressUnits(
    enrolment
  ) {
    const key =
      String(
        enrolment?.id ||
        ''
      );

    if (
      progressUnitCache
        .has(key)
    ) {
      return progressUnitCache
        .get(key);
    }

    const result =
      progressAssignmentContext
        ? buildProgressAssessmentUnits({
            courseId:
              course.id,

            enrolment,

            context:
              progressAssignmentContext,

            submissions:
              assignmentSubmissions,

            completions:
              assignmentCompletions
          })
        : [];

    progressUnitCache.set(
      key,
      result
    );

    return result;
  }


  function getEffectiveAssignmentScore(
    enrolmentId,
    submission,
    feedback = null
  ) {
    if (!submission?.id) {
      return null;
    }

    const resolvedFeedback =
      feedback ||
      latestProgressFeedback(
        submission
      );

    const override =
      assignmentGradeOverrides
        .find(
          row =>
            String(
              row.enrolment_id
            ) ===
              String(
                enrolmentId
              ) &&
            String(
              row
                .assignment_submission_id
            ) ===
              String(
                submission.id
              )
        );

    const rawScore =
      override?.score !==
        null &&
      override?.score !==
        undefined
        ? override.score
        : resolvedFeedback?.score;

    if (
      rawScore === null ||
      rawScore === undefined ||
      rawScore === ''
    ) {
      return null;
    }

    const numeric =
      Number(
        rawScore
      );

    return Number.isFinite(
      numeric
    )
      ? numeric
      : null;
  }


  function getTraditionalScoreSoFar(
    enrolment
  ) {
    if (
      course
        .grading_mode !==
      'traditional'
    ) {
      return null;
    }

    const today =
      getToday();

    let earnedPoints =
      0;

    let duePoints =
      0;

    // --------------------------------------------------------
    // Ordinary / scheduled / Structured Form assignments
    // --------------------------------------------------------

    const units =
      getProgressUnits(
        enrolment
      )
        .filter(
          unit =>
            unit
              .requiresSubmission !==
            false
        );

    const unitsByType =
      new Map();

    units.forEach(
      unit => {
        const key =
          String(
            unit.typeId ||
            ''
          );

        if (
          !unitsByType.has(
            key
          )
        ) {
          unitsByType.set(
            key,
            []
          );
        }

        unitsByType
          .get(key)
          .push(unit);
      }
    );

    unitsByType.forEach(
      (
        typeUnits,
        typeId
      ) => {
        const weight =
          getTraditionalWeight(
            typeId
          );

        if (
          !weight ||
          typeUnits.length ===
          0
        ) {
          return;
        }

        const pointsPerUnit =
          weight /
          typeUnits.length;

        const dueUnits =
          typeUnits
            .filter(
              unit =>
                unit.dueDate &&
                String(
                  unit.dueDate
                ).slice(0, 10) <=
                  today
            );

        duePoints +=
          dueUnits.length *
          pointsPerUnit;

        dueUnits.forEach(
          unit => {
            const feedback =
              latestProgressFeedback(
                unit.submission
              );

            if (
              feedback
                ?.submitted !==
              true
            ) {
              return;
            }

            const score =
              getEffectiveAssignmentScore(
                enrolment.id,
                unit.submission,
                feedback
              );

            if (
              score === null
            ) {
              return;
            }

            earnedPoints +=
              (
                score /
                100
              ) *
              pointsPerUnit;
          }
        );
      }
    );

    // --------------------------------------------------------
    // Attendance
    // Same calculation used on the student Progress page.
    // --------------------------------------------------------

    if (
      course
        .attendance_enabled !==
      false
    ) {
      const allAttendanceWeeks =
        getAttendanceWeeksForEnrolment(
          enrolment,
          weeks
        );

      const pastAttendanceWeeks =
        allAttendanceWeeks
          .filter(
            week =>
              week.class_date <=
              today
          );

      const pastIds =
        new Set(
          pastAttendanceWeeks
            .map(
              week =>
                week.id
            )
        );

      const attended =
        new Set(
          attendance
            .filter(
              row =>
                row
                  .enrolment_id ===
                  enrolment.id &&
                row.present &&
                pastIds.has(
                  row.week_id
                )
            )
            .map(
              row =>
                row.week_id
            )
        ).size;

      const weight =
        getTraditionalWeightByName(
          'Attendance'
        );

      const absences =
        Math.max(
          0,
          pastAttendanceWeeks
            .length -
            attended
        );

      const settings =
        normalizeAttendanceSettings(
          course
        );

      const currentContribution =
        allAttendanceWeeks.length >
        0
          ? calculateAttendanceContribution({
              absences,

              totalRequired:
                allAttendanceWeeks
                  .length,

              weight,

              ...settings
            })
          : 0;

      const attendanceDue =
        allAttendanceWeeks.length >
        0
          ? weight *
            (
              pastAttendanceWeeks
                .length /
              allAttendanceWeeks
                .length
            )
          : 0;

      const penalty =
        weight -
        currentContribution;

      const attendanceEarned =
        pastAttendanceWeeks.length >
        0
          ? attendanceDue -
            penalty
          : 0;

      duePoints +=
        attendanceDue;

      earnedPoints +=
        attendanceEarned;
    }

    // --------------------------------------------------------
    // Critical Questions
    // --------------------------------------------------------

    if (
      course
        .cq_enabled !==
      false
    ) {
      const allCqWeeks =
        weeks.filter(
          week =>
            week.cq_enabled
        );

      const pastCqWeeks =
        allCqWeeks.filter(
          week =>
            week.class_date <=
            today
        );

      const pastIds =
        new Set(
          pastCqWeeks.map(
            week =>
              week.id
          )
        );

      const completedIds =
        new Set([
          ...cqSubmissions
            .filter(
              row =>
                row
                  .enrolment_id ===
                  enrolment.id &&
                pastIds.has(
                  row.week_id
                )
            )
            .map(
              row =>
                row.week_id
            ),

          ...cqManualSubmissionMarks
            .filter(
              row =>
                String(
                  row.enrolment_id
                ) ===
                  String(
                    enrolment.id
                  ) &&
                pastIds.has(
                  row.week_id
                )
            )
            .map(
              row =>
                row.week_id
            )
        ]);

      const weight =
        getTraditionalWeightByName(
          'Critical Questions'
        );

      const pointsPerWeek =
        allCqWeeks.length > 0
          ? weight /
            allCqWeeks.length
          : 0;

      duePoints +=
        pastCqWeeks.length *
        pointsPerWeek;

      earnedPoints +=
        completedIds.size *
        pointsPerWeek;
    }

    // --------------------------------------------------------
    // Philosophical Diary
    // --------------------------------------------------------

    if (
      course
        .pd_enabled !==
      false
    ) {
      const allPdWeeks =
        weeks.filter(
          week =>
            week.pd_enabled
        );

      const duePdWeeks =
        allPdWeeks.filter(
          week => {
            const submission =
              pdSubmissions.find(
                row =>
                  row
                    .enrolment_id ===
                    enrolment.id &&
                  row.week_id ===
                    week.id
              );

            const dueDate =
              getProgressPdDueDate(
                course,
                week,
                submission
              );

            return (
              !!dueDate &&
              dueDate <= today
            );
          }
        );

      const weight =
        getTraditionalWeightByName(
          'Philosophical Diary'
        );

      const pointsPerWeek =
        allPdWeeks.length > 0
          ? weight /
            allPdWeeks.length
          : 0;

      duePoints +=
        duePdWeeks.length *
        pointsPerWeek;

      duePdWeeks.forEach(
        week => {
          const submission =
            pdSubmissions.find(
              row =>
                row
                  .enrolment_id ===
                  enrolment.id &&
                row.week_id ===
                  week.id
            );

          const feedback =
            getSubmittedPdFeedback(
              submission
            );

          const score =
            Number(
              feedback?.score
            );

          if (
            !Number.isFinite(
              score
            )
          ) {
            return;
          }

          earnedPoints +=
            (
              score /
              100
            ) *
            pointsPerWeek;
        }
      );
    }

    if (
      duePoints <= 0
    ) {
      return null;
    }

    return (
      earnedPoints /
      duePoints
    ) * 100;
  }

  function assignmentRequiresSubmission(
    assignment
  ) {
    return (
      assignment
        ?.requires_submission ??
      assignment
        ?.assignment_types
        ?.requires_submission ??
      true
    );
  }


  function progressAssignmentLabel(
    assignment
  ) {
    const base =
      assignment
        ?.assignment_types
        ?.name ||
      'Assignment';

    const sameTypeCount =
      courseAssignments
        .filter(
          candidate =>
            String(
              candidate
                .assignment_type_id
            ) ===
              String(
                assignment
                  .assignment_type_id
              )
        )
        .length;

    return (
      base +
      (
        sameTypeCount > 1
          ? (
              ' ' +
              String(
                assignment
                  .instance_number ||
                ''
              )
            )
          : ''
      )
    );
  }


  function getAssignmentProgress(
    enrolment,
    assignment
  ) {
    const units =
      getProgressUnits(
        enrolment
      )
        .filter(
          unit =>
            String(
              unit.assignmentId
            ) ===
              String(
                assignment.id
              )
        );

    return {
      units,

      summary:
        summariseProgressUnits(
          units,
          getToday()
        )
    };
  }


  function getAssignmentCellPresentation(
    enrolment,
    assignment
  ) {
    const {
      units,
      summary
    } =
      getAssignmentProgress(
        enrolment,
        assignment
      );

    if (
      summary.total ===
      0
    ) {
      return {
        value: '—',
        color: 'grey'
      };
    }

    if (
      !assignmentRequiresSubmission(
        assignment
      )
    ) {
      if (
        summary.total ===
        1
      ) {
        return {
          value:
            summary.completed
              ? '✓'
              : '—',

          color:
            summary.completed
              ? 'green'
              : 'grey'
        };
      }

      return {
        value:
          String(
            summary.completed
          ) +
          '/' +
          String(
            summary.total
          ),

        color:
          summary.completed ===
            summary.total
            ? 'green'
            : (
                summary.completed > 0
                  ? 'blue'
                  : 'grey'
              )
      };
    }

    // Traditional grading shows the actual score.
    if (
      course
        .grading_mode ===
      'traditional'
    ) {
      const scoredUnits =
        units
          .map(
            unit => {
              const feedback =
                latestProgressFeedback(
                  unit.submission
                );

              const score =
                getEffectiveAssignmentScore(
                  enrolment.id,
                  unit.submission,
                  feedback
                );

              if (
                feedback?.submitted !==
                  true ||
                score === null
              ) {
                return null;
              }

              return score;
            }
          )
          .filter(
            score =>
              score !==
              null
          );

      if (
        scoredUnits.length >
        0
      ) {
        const average =
          scoredUnits.reduce(
            (
              sum,
              score
            ) =>
              sum + score,
            0
          ) /
          scoredUnits.length;

        const scoreLabel =
          formatTraditionalScore(
            average
          ) + '%';

        if (
          summary.total ===
          1
        ) {
          return {
            value:
              scoreLabel,

            color:
              'green'
          };
        }

        return {
          value:
            scoreLabel +
            ' · ' +
            String(
              scoredUnits.length
            ) +
            '/' +
            String(
              summary.total
            ) +
            ' graded',

          color:
            scoredUnits.length ===
              summary.total
              ? 'green'
              : 'blue'
        };
      }

      if (
        summary.submitted >
        0
      ) {
        return {
          value:
            summary.total ===
            1
              ? 'Submitted'
              : (
                  String(
                    summary.submitted
                  ) +
                  '/' +
                  String(
                    summary.total
                  ) +
                  ' submitted'
                ),

          color:
            'blue'
        };
      }

      return {
        value: '—',
        color: 'grey'
      };
    }

    // Ungrading / non-traditional behaviour remains unchanged.
    if (
      summary.total ===
      1
    ) {
      if (summary.submitted === 1 && summary.completed === 0) {
        return { value: 'Incomplete', color: 'red' };
      }
      if (summary.graded === 1) {
        return { value: 'Graded', color: 'green' };
      }

      if (
        summary.submitted ===
        1
      ) {
        return {
          value: 'Submitted',
          color: 'blue'
        };
      }

      return {
        value: '—',
        color: 'grey'
      };
    }

    const gradedSuffix =
      summary.graded > 0
        ? (
            ' · ' +
            String(
              summary.graded
            ) +
            ' graded'
          )
        : '';

    return {
      value:
        String(summary.completed) + '/' + String(summary.total) + gradedSuffix,

      color:
        summary.completed === summary.total &&
        summary.total > 0
          ? 'green'
          : (
              summary.submitted > 0
                ? 'blue'
                : 'grey'
            )
    };
  }


  function progressUnitStatus(
    unit,
    enrolmentId
  ) {
    if (
      unit
        .requiresSubmission ===
      false
    ) {
      return unit.completed
        ? 'Complete'
        : 'Not complete';
    }

    const feedback =
      latestProgressFeedback(
        unit.submission
      );

    if (
      unit.submitted &&
      feedback?.submitted
    ) {
      if (
        course
          .grading_mode ===
        'traditional'
      ) {
        const score =
          formatTraditionalScore(
            getEffectiveAssignmentScore(
              enrolmentId,
              unit.submission,
              feedback
            )
          );

        return score !== null
          ? score + '%'
          : 'Score missing';
      }

      return unit.completed ? 'Graded' : 'Incomplete';
    }

    if (unit.submitted) {
      return 'Submitted';
    }

    if (unit.choiceMissing) {
      return 'Schedule not chosen';
    }

    return 'Not submitted';
  }


  function formatProgressDate(
    value
  ) {
    if (!value) {
      return '';
    }

    return String(value)
      .slice(0, 10);
  }


  const horizontalScroll =
    useTopHorizontalScrollbar(
      [
        courseAssignments.length,
        enrolments.length,

        course
          .attendance_enabled !==
          false
          ? 1
          : 0,

        course
          .cq_enabled !==
          false
          ? 1
          : 0,

        course
          .qi_enabled ===
          true
          ? 1
          : 0,

        course
          .pd_enabled !==
          false
          ? 1
          : 0,

        course.grading_mode,

        course
          .self_eval_enabled ===
          true
          ? 1
          : 0
      ].join(':')
    );

  if (loading) return <div style={styles.muted}>Loading...</div>;

  if (embeddedEnrolmentId) {
    const enrolment = enrolments.find(row =>
      String(row.id) === String(embeddedEnrolmentId)
    );

    if (!enrolment) {
      return <div style={styles.muted}>Student progress unavailable.</div>;
    }

    // These are the existing Student Progress calculations,
    // not a second set of progress rules.
    const stats = getStats(enrolment);
    const notes = [];
    const incompleteItems = [];
    const incompleteComments = new Map();

    const addIncomplete = (label, comment) => {
      incompleteItems.push(label);

      const text = readableNote(comment);
      if (!text) return;

      const existing = incompleteComments.get(label) || [];
      if (!existing.includes(text)) {
        incompleteComments.set(label, [...existing, text]);
      }
    };
    const seenNotes = new Set();

    const readableNote = value =>
      String(value || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(?:p|div)>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .trim();

    const addNote = (label, value) => {
      const note = readableNote(value);
      if (!note) return;

      const key = label + '|' + note;
      if (seenNotes.has(key)) return;
      seenNotes.add(key);
      notes.push({ label, note });
    };

    const isIncomplete = feedback =>
      String(feedback?.completion_status || '')
        .trim()
        .toLowerCase() === 'incomplete';

    const latestRow = rows => {
      const list = Array.isArray(rows)
        ? rows
        : rows ? [rows] : [];

      return [...list].sort((a, b) =>
        new Date(b.submitted_at || b.updated_at || b.created_at || 0).getTime() -
        new Date(a.submitted_at || a.updated_at || a.created_at || 0).getTime()
      )[0] || null;
    };

    const matchesStudent = row =>
      String(row?.enrolment_id || '') === String(enrolment.id) ||
      (
        Array.isArray(row?.group_member_ids) &&
        row.group_member_ids.some(id =>
          String(id) === String(enrolment.id)
        )
      );

    // Collect diary notes and Incomplete outcomes.
    pdSubmissions
      .filter(row => String(row.enrolment_id) === String(enrolment.id))
      .forEach(row => {
        const week = weeks.find(w => w.id === row.week_id);
        const label = 'Philosophical Diary' +
          (week ? ' — Week ' + week.week_number : '');

        const feedbackRows = Array.isArray(row.pd_feedback)
          ? row.pd_feedback
          : row.pd_feedback ? [row.pd_feedback] : [];

        feedbackRows.forEach(feedback =>
          addNote(label, feedback.instructor_note)
        );

        const latestFeedback = latestRow(feedbackRows);

        if (isIncomplete(latestFeedback)) {
          addIncomplete(label, latestFeedback?.comment);
        }
      });

    // Also preserve instructor notes recorded directly
    // against CQ or QI submissions, when present.
    cqSubmissions
      .filter(matchesStudent)
      .forEach(row => {
        const week = weeks.find(w => w.id === row.week_id);
        const label = 'Critical Questions' +
          (week ? ' — Week ' + week.week_number : '');

        addNote(label, row.instructor_note);

        if (isIncomplete(row)) addIncomplete(label, row.comment);
      });

    qiSubmissions
      .filter(matchesStudent)
      .forEach(row => {
        const assignment = qiAssignments.find(a =>
          String(a.id) === String(row.cq_question_assignment_id)
        );
        const week = weeks.find(w =>
          w.id === assignment?.course_week_id
        );
        const label = 'Question Improvement' +
          (week ? ' — Week ' + week.week_number : '');

        addNote(label, row.instructor_note);

        if (isIncomplete(row)) addIncomplete(label, row.comment);
      });

    // Other Assignments: aggregate the exact units used
    // by Student Progress, grouped by assignment type.
    const today = getToday();

    const units = getProgressUnits(enrolment).filter(unit => {
      const dueDate = String(unit.dueDate || '').slice(0, 10);
      const isDue = !!dueDate && dueDate <= today;

      const feedback = unit.submission?.assignment_feedback;
      const feedbackRows = Array.isArray(feedback)
        ? feedback
        : feedback ? [feedback] : [];

      const hasPublishedFeedback = feedbackRows.some(
        row => row?.submitted === true
      );

      return (
        isDue ||
        unit.submitted === true ||
        unit.completed === true ||
        hasPublishedFeedback
      );
    });

    const byType = new Map();

    courseAssignments
      .filter(a => a.assignment_types?.is_core !== true)
      .forEach(assignment => {
        const title = assignment.assignment_types?.name || 'Assignment';

        const relevant = units.filter(unit =>
          String(unit.assignmentId) === String(assignment.id)
        );

        if (!relevant.length) return;

        if (!byType.has(title)) {
          byType.set(title, {
            title,
            completed: 0,
            total: 0,
            incomplete: 0
          });
        }

        const group = byType.get(title);
        const summary = summariseProgressUnits(relevant, today);

        group.completed += summary.completed;
        group.total += summary.total;

        relevant.forEach(unit => {
          const feedbackRows = unit.submission?.assignment_feedback;
          const feedback = latestProgressFeedback(unit.submission);
          const label = title +
            (
              unit.detailLabel &&
              unit.detailLabel !== 'Assignment' &&
              unit.detailLabel !== 'Completion'
                ? ' — ' + unit.detailLabel
                : ''
            );

          const allFeedback = Array.isArray(feedbackRows)
            ? feedbackRows
            : feedbackRows ? [feedbackRows] : [];

          allFeedback.forEach(row =>
            addNote(label, row.instructor_note)
          );

          if (isIncomplete(feedback)) {
            group.incomplete++;
            addIncomplete(label, feedback?.comment);
          }
        });
      });

    const otherGroups = [...byType.values()];
    const otherCompleted = otherGroups.reduce(
      (total, group) => total + group.completed, 0
    );
    const otherTotal = otherGroups.reduce(
      (total, group) => total + group.total, 0
    );

    const pdIncomplete = incompleteItems.filter(label =>
      label.startsWith('Philosophical Diary')
    ).length;

    const builtIns = [
      course.attendance_enabled !== false && {
        label: 'Attendance',
        value: stats.attPresent + '/' + stats.attTotal
      },
      course.cq_enabled !== false && {
        label: 'Critical Questions',
        value: stats.cqCount + '/' + stats.cqTotal
      },
      course.qi_enabled === true &&
        course.cq_enabled !== false && {
        label: 'Question Improvement',
        value: stats.qiCount + '/' + stats.qiTotal
      },
      course.pd_enabled !== false && {
        label: 'Philosophical Diary',
        value: stats.pdCount + '/' + stats.pdTotal,
        incomplete: pdIncomplete
      }
    ].filter(Boolean);

    const summaryRow = (label, value, incomplete = 0) => (
      <div
        key={label}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '7px 0',
          borderBottom: '1px solid #e4e9ed'
        }}
      >
        <span style={{ fontSize: '13px' }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 700 }}>
          {value}
          {incomplete > 0 && (
            <span style={{
              color: '#a12a2a',
              marginLeft: '10px'
            }}>
              Incomplete ({incomplete})
            </span>
          )}
        </span>
      </div>
    );

    return (
      <div style={{ width: '100%', minWidth: 0 }}>
        {builtIns.map(row =>
          summaryRow(row.label, row.value, row.incomplete || 0)
        )}

        <div style={{
          marginTop: '12px',
          fontWeight: 700,
          fontSize: '13px'
        }}>
          Other Assignments
          <span style={{ marginLeft: '10px' }}>
            {otherTotal ? otherCompleted + '/' + otherTotal : '—'}
          </span>
        </div>

        {otherGroups.length === 0 && (
          <div style={styles.muted}>No other assignments.</div>
        )}

        {otherGroups.map(group =>
          summaryRow(
            group.title,
            group.total
              ? group.completed + '/' + group.total
              : 'Not applicable',
            group.incomplete
          )
        )}

        {incompleteItems.length > 0 && (
          <div style={{
            marginTop: '14px',
            fontSize: '13px'
          }}>
            <strong>Items marked Incomplete:</strong>
            {[...new Set(incompleteItems)].map(label => (
              <div
                key={label}
                style={{
                  marginTop: '8px',
                  padding: '8px 10px',
                  border: '1px solid #e4e9ed',
                  borderRadius: '5px',
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid'
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {label}
                </div>

                {(incompleteComments.get(label) || []).map(
                  (comment, index) => (
                    <div
                      key={index}
                      style={{
                        marginTop: '5px',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.5
                      }}
                    >
                      {comment}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}

        {notes.length > 0 && (
          <section style={{ marginTop: '16px' }}>
            <h4 style={{
              margin: '0 0 8px',
              fontSize: '14px'
            }}>
              Instructor Notes
            </h4>

            {notes.map((item, index) => (
              <div
                key={index}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid #e4e9ed'
                }}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  marginBottom: '4px'
                }}>
                  {item.label}
                </div>
                <div style={{
                  fontSize: '13px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere'
                }}>
                  {item.note}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* View panel */}
      {viewStudent && (
        <div style={styles.overlay}>
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>
                  {getStudentName(
                    viewStudent.students
                  )}
                </h2>

                <div style={styles.panelSub}>
                  {
                    viewStudent.students
                      ?.student_code
                  }
                </div>
              </div>

              <button
                style={styles.closeBtn}
                onClick={() =>
                  setViewStudent(null)
                }
              >
                ×
              </button>
            </div>

            <div style={styles.panelBody}>
              {course.attendance_enabled !== false && (
                <div style={styles.section}>
                  <h3 style={styles.sectionHeading}>
                    Attendance
                  </h3>

                  <div style={styles.attGrid}>
                    {getAttendanceDetail(
                      viewStudent
                    ).map(
                      ({
                        week,
                        present
                      }) => (
                        <div
                          key={week.id}
                          style={{
                            ...styles.attCell,

                            background:
                              present
                                ? '#e7f5ed'
                                : '#faeaea',

                            color:
                              present
                                ? '#216e45'
                                : '#a12a2a'
                          }}
                        >
                          <div style={styles.attWeekNum}>
                            W{week.week_number}
                          </div>

                          <div style={styles.attWeekDate}>
                            {week.class_date.slice(5)}
                          </div>

                          <div>
                            {present ? '✓' : '✗'}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {course.cq_enabled !== false && (
                <div style={styles.section}>
                  <h3 style={styles.sectionHeading}>
                    Critical Questions
                  </h3>

                  {viewCqLoading && (
                    <div style={styles.muted}>
                      Loading...
                    </div>
                  )}

                  {!viewCqLoading &&
                    viewCqSubs.length === 0 && (
                    <div style={styles.muted}>
                      No question text submitted.
                    </div>
                  )}

                  {!viewCqLoading &&
                    viewCqSubs.map(
                      cq => (
                        <div
                          key={cq.id}
                          style={styles.cqItem}
                        >
                          <div style={styles.cqMeta}>
                            Week {
                              cq.course_weeks
                                ?.week_number
                            }
                            {' · '}
                            {cq.question_type}
                          </div>

                          <div style={styles.cqText}>
                            {cq.question_text}
                          </div>
                        </div>
                      )
                    )}

                  {cqManualSubmissionMarks
                    .filter(
                      row =>
                        String(
                          row.enrolment_id
                        ) ===
                          String(
                            viewStudent.id
                          )
                    )
                    .length > 0 && (
                    <div style={styles.muted}>
                      {
                        cqManualSubmissionMarks
                          .filter(
                            row =>
                              String(
                                row.enrolment_id
                              ) ===
                                String(
                                  viewStudent.id
                                )
                          )
                          .length
                      } submission(s) marked manually.
                    </div>
                  )}
                </div>
              )}

              {course.qi_enabled === true &&
                course.cq_enabled !== false && (
                <div style={styles.section}>
                  <h3 style={styles.sectionHeading}>
                    Question Improvement
                  </h3>

                  {getQiDetail(
                    viewStudent
                  ).length === 0 && (
                    <div style={styles.muted}>
                      No Question Improvement assignments to date.
                    </div>
                  )}

                  {getQiDetail(
                    viewStudent
                  ).map(
                    ({
                      week,
                      submitted
                    }) => (
                      <div
                        key={week.id}
                        style={styles.assignmentItem}
                      >
                        <div style={styles.assignmentLabel}>
                          Week {week.week_number}
                        </div>

                        <div
                          style={
                            submitted
                              ? styles.completedTag
                              : styles.notCompletedTag
                          }
                        >
                          {
                            submitted
                              ? 'Submitted'
                              : 'Not submitted'
                          }
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

              {course.pd_enabled !== false && (
                <div style={styles.section}>
                  <h3 style={styles.sectionHeading}>
                    Philosophical Diary
                  </h3>

                  {getPdDetail(
                    viewStudent
                  ).length === 0 && (
                    <div style={styles.muted}>
                      No PD submissions.
                    </div>
                  )}

                  {getPdDetail(
                    viewStudent
                  ).map(
                    pd => (
                      <div
                        key={pd.id}
                        style={styles.pdItem}
                      >
                        <div style={styles.pdMeta}>
                          Week {
                            pd.week
                              ?.week_number
                          }

                          {pd.pd_feedback
                            ?.submitted && (
                            <span style={styles.gradedTag}>
                              Commented
                            </span>
                          )}
                        </div>

                        <div style={styles.pdText}>
                          {pd.text}
                        </div>

                        {pd.pd_feedback
                          ?.comment && (
                          <div style={styles.feedbackBox}>
                            <div style={styles.feedbackLabel}>
                              Instructor comment
                            </div>

                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  pd.pd_feedback
                                    .comment
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}

              {courseAssignments.length > 0 && (
                <div style={styles.section}>
                  <h3 style={styles.sectionHeading}>
                    Other Assignments
                  </h3>

                  {courseAssignments.map(
                    assignment => {
                      const label =
                        progressAssignmentLabel(
                          assignment
                        );

                      const {
                        units
                      } =
                        getAssignmentProgress(
                          viewStudent,
                          assignment
                        );

                      if (
                        units.length ===
                        0
                      ) {
                        return (
                          <div
                            key={assignment.id}
                            style={styles.pdItem}
                          >
                            <div style={styles.pdMeta}>
                              {label}
                            </div>

                            <div style={styles.muted}>
                              Not applicable to this student.
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={assignment.id}
                          style={styles.pdItem}
                        >
                          <div style={styles.pdMeta}>
                            {label}
                          </div>

                          {units.map(
                            unit => {
                              const feedback =
                                latestProgressFeedback(
                                  unit.submission
                                );

                              const complete = unit.completed;

                              return (
                                <div
                                  key={unit.key}
                                  style={{
                                    ...styles.assignmentItem,
                                    display: 'block'
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      gap: '12px',
                                      alignItems: 'flex-start'
                                    }}
                                  >
                                    <div>
                                      <div style={styles.assignmentLabel}>
                                        {unit.detailLabel}
                                      </div>

                                      {unit.dueDate && (
                                        <div style={styles.muted}>
                                          Due {
                                            formatProgressDate(
                                              unit.dueDate
                                            )
                                          }
                                        </div>
                                      )}
                                    </div>

                                    <div
                                      style={
                                        complete
                                          ? (
                                              feedback
                                                ?.submitted
                                                ? styles.completedTag
                                                : styles.submittedTag
                                            )
                                          : styles.notCompletedTag
                                      }
                                    >
                                      {
                                        progressUnitStatus(
                                          unit,
                                          viewStudent.id
                                        )
                                      }
                                    </div>
                                  </div>

                                  {unit.submission?.text && (
                                    <div style={styles.pdText}>
                                      {
                                        unit.submission
                                          .text
                                      }
                                    </div>
                                  )}

                                  {unit.submission
                                    ?.drive_file_id && (
                                    <a
                                      href={
                                        'https://drive.google.com/uc?export=download&id=' +
                                        unit.submission
                                          .drive_file_id
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                      style={styles.downloadLink}
                                    >
                                      Download submitted file
                                    </a>
                                  )}

                                  {feedback?.comment && (
                                    <div style={styles.feedbackBox}>
                                      <div style={styles.feedbackLabel}>
                                        Instructor comment
                                      </div>

                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            feedback.comment
                                        }}
                                      />
                                    </div>
                                  )}

                                  {feedback
                                    ?.instructor_note && (
                                    <div
                                      style={{
                                        ...styles.feedbackBox,
                                        background: '#fff4d7',
                                        borderColor: '#ead494'
                                      }}
                                    >
                                      <div style={styles.feedbackLabel}>
                                        Instructor note (internal)
                                      </div>

                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            feedback
                                              .instructor_note
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              )}

              {course.self_eval_enabled === true &&
                ['Mid-Semester', 'Final'].map(
                  section => {
                    const response =
                      selfEvalResponses.find(
                        row =>
                          row.enrolment_id ===
                            viewStudent.id &&
                          row.section ===
                            section
                      );

                    if (
                      !response ||
                      response.status !==
                        'Submitted'
                    ) {
                      return null;
                    }

                    return (
                      <div
                        key={section}
                        style={styles.section}
                      >
                        <h3 style={styles.sectionHeading}>
                          Self-Evaluation — {section}
                        </h3>

                        {response.final_grade && (
                          <div style={styles.finalGradeBox}>
                            Final grade:{' '}
                            <strong>
                              {
                                response
                                  .final_grade
                              }
                            </strong>
                          </div>
                        )}

                        <div style={styles.muted}>
                          Submitted {
                            response
                              .submitted_at
                              ?.slice(0, 10)
                          }
                        </div>
                      </div>
                    );
                  }
                )}
            </div>
          </div>
        </div>
      )}

      {/* Progress table */}
      <h2 style={styles.subheading}>
        Student Progress
      </h2>

      <p style={styles.muted}>
        Counts reflect each student's applicable work and effective deadlines.
      </p>

      {refreshError && (
        <div
          style={{
            ...styles.muted,
            color: '#a12a2a'
          }}
        >
          {refreshError}
        </div>
      )}

      <div
        ref={
          horizontalScroll
            .topScrollRef
        }
        style={{
          ...styles
                .topHorizontalScroll,

              left:
                horizontalScroll
                  .floatingLeft +
                'px',

              width:
                horizontalScroll
                  .floatingWidth +
                'px',

              bottom:
                horizontalScroll
                  .floatingBottom +
                'px',

          ...(
            horizontalScroll
                  .needsScroll &&
                horizontalScroll
                  .floatingVisible
                  ? {}
                  : styles
                      .topHorizontalScrollHidden
          )
        }}
        onScroll={
          horizontalScroll
            .onTopScroll
        }
        aria-hidden={
              !(
                horizontalScroll
                  .needsScroll &&
                horizontalScroll
                  .floatingVisible
              )
            }
      >
        <div
          style={{
            ...styles
              .topHorizontalScrollTrack,

            width:
              Math.max(
                1,
                horizontalScroll
                  .scrollWidth
              ) +
              'px'
          }}
        />
      </div>

      <div
        ref={
          horizontalScroll
            .contentScrollRef
        }
        style={
          styles.tableWrap
        }
        onScroll={
          horizontalScroll
            .onContentScroll
        }
      >
        <table
          style={{
            ...styles.table,

            minWidth:
              Math.max(
                760,

                300 +
                (
                  course.attendance_enabled !==
                    false
                    ? 92
                    : 0
                ) +
                (
                  course.cq_enabled !==
                    false
                    ? 70
                    : 0
                ) +
                (
                  course.qi_enabled ===
                    true
                    ? 70
                    : 0
                ) +
                (
                  course.pd_enabled !==
                    false
                    ? 70
                    : 0
                ) +
                courseAssignments.length *
                  130 +
                (
                  course.grading_mode ===
                    'traditional'
                    ? 105
                    : 0
                ) +
                (
                  course.self_eval_enabled ===
                    true
                    ? 180
                    : 0
                )
              ) + 'px'
          }}
        >
          <colgroup>
            <col style={{ width: '220px' }} />

            {course.attendance_enabled !== false && (
              <col style={{ width: '92px' }} />
            )}

            {course.cq_enabled !== false && (
              <col style={{ width: '70px' }} />
            )}

            {course.qi_enabled === true && (
              <col style={{ width: '70px' }} />
            )}

            {course.pd_enabled !== false && (
              <col style={{ width: '70px' }} />
            )}

            {courseAssignments.map(
              assignment => (
                <col
                  key={
                    'col-' +
                    assignment.id
                  }
                  style={{
                    width: '130px'
                  }}
                />
              )
            )}

            {course.grading_mode === 'traditional' && (
              <col style={{ width: '105px' }} />
            )}

            {course.self_eval_enabled === true && (
              <>
                <col style={{ width: '90px' }} />
                <col style={{ width: '90px' }} />
              </>
            )}

            <col style={{ width: '80px' }} />
          </colgroup>

          <thead>
            <tr>
              <th
                style={{
                  ...styles.th,
                  ...styles.thStudent
                }}
              >
                Student
              </th>

              {course.attendance_enabled !== false && (
                <th style={styles.th}>
                  Attendance
                </th>
              )}

              {course.cq_enabled !== false && (
                <th style={styles.th}>
                  CQ
                </th>
              )}

              {course.qi_enabled === true && (
                <th style={styles.th}>
                  QI
                </th>
              )}

              {course.pd_enabled !== false && (
                <th style={styles.th}>
                  PD
                </th>
              )}

              {courseAssignments.map(
                assignment => {
                  const label =
                    progressAssignmentLabel(
                      assignment
                    );

                  return (
                    <th
                      key={assignment.id}
                      style={{
                        ...styles.th,
                        ...styles.thAssignment
                      }}
                      title={label}
                    >
                      {label}
                    </th>
                  );
                }
              )}

              {course.grading_mode === 'traditional' && (
                <th
                  style={{
                    ...styles.th,
                    ...styles.thSummary
                  }}
                >
                  Score so far
                </th>
              )}

              {course.self_eval_enabled === true && (
                <>
                  <th
                    style={{
                      ...styles.th,
                      ...styles.thSummary
                    }}
                  >
                    Mid Eval
                  </th>

                  <th
                    style={{
                      ...styles.th,
                      ...styles.thSummary
                    }}
                  >
                    Final Eval
                  </th>
                </>
              )}

              <th
                style={{
                  ...styles.th,
                  ...styles.thDetails
                }}
              >
                Details
              </th>
            </tr>
          </thead>

          <tbody>
            {enrolments.map(
              enrolment => {
                const stats =
                  getStats(
                    enrolment
                  );

                const student =
                  enrolment.students;

                const scoreSoFar =
                  course.grading_mode ===
                    'traditional'
                    ? getTraditionalScoreSoFar(
                        enrolment
                      )
                    : null;

                return (
                  <tr key={enrolment.id}>
                    <td
                      style={styles.tdName}
                      title={
                        getStudentIdentity(
                          student
                        )
                      }
                    >
                      <div style={styles.studentPrimary}>
                        {
                          student
                            ?.english_name ||
                          student
                            ?.chinese_name ||
                          student
                            ?.student_code ||
                          '—'
                        }
                      </div>

                      <div style={styles.studentSecondary}>
                        {[
                          student
                            ?.student_code,

                          student
                            ?.english_name &&
                          student
                            ?.chinese_name
                            ? student
                                .chinese_name
                            : null
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>

                    {course.attendance_enabled !== false && (
                      <td style={styles.td}>
                        <StatPill
                          value={
                            stats.attPresent
                          }
                          max={
                            stats.attTotal
                          }
                          color="blue"
                        />
                      </td>
                    )}

                    {course.cq_enabled !== false && (
                      <td style={styles.td}>
                        <StatPill
                          value={
                            stats.cqCount
                          }
                          max={
                            stats.cqTotal
                          }
                          color="blue"
                        />
                      </td>
                    )}

                    {course.qi_enabled === true && (
                      <td style={styles.td}>
                        <StatPill
                          value={
                            stats.qiCount
                          }
                          max={
                            stats.qiTotal
                          }
                          color="blue"
                        />
                      </td>
                    )}

                    {course.pd_enabled !== false && (
                      <td style={styles.td}>
                        <StatPill
                          value={
                            stats.pdCount
                          }
                          max={
                            stats.pdTotal
                          }
                          color="blue"
                        />
                      </td>
                    )}

                    {courseAssignments.map(
                      assignment => {
                        const presentation =
                          getAssignmentCellPresentation(
                            enrolment,
                            assignment
                          );

                        return (
                          <td
                            key={assignment.id}
                            style={styles.td}
                          >
                            <StatPill
                              value={
                                presentation
                                  .value
                              }
                              color={
                                presentation
                                  .color
                              }
                            />
                          </td>
                        );
                      }
                    )}

                    {course.grading_mode === 'traditional' && (
                      <td style={styles.td}>
                        <StatPill
                          value={
                            scoreSoFar ===
                            null
                              ? '—'
                              : (
                                  formatTraditionalScore(
                                    scoreSoFar
                                  ) +
                                  '%'
                                )
                          }
                          color="blue"
                        />
                      </td>
                    )}

                    {course.self_eval_enabled === true && (
                      <>
                        <td style={styles.td}>
                          {stats
                            .selfEvalMid
                            ?.status ===
                            'Submitted'
                            ? (
                                <StatPill
                                  value={
                                    stats
                                      .selfEvalMid
                                      .final_grade ||
                                    '✓'
                                  }
                                  color="green"
                                />
                              )
                            : (
                                <StatPill
                                  value="—"
                                  color="grey"
                                />
                              )}
                        </td>

                        <td style={styles.td}>
                          {stats
                            .selfEvalFinal
                            ?.status ===
                            'Submitted'
                            ? (
                                <StatPill
                                  value={
                                    stats
                                      .selfEvalFinal
                                      .final_grade ||
                                    '✓'
                                  }
                                  color="green"
                                />
                              )
                            : (
                                <StatPill
                                  value="—"
                                  color="grey"
                                />
                              )}
                        </td>
                      </>
                    )}

                    <td style={styles.td}>
                      <button
                        style={styles.viewBtn}
                        onClick={() =>
                          handleViewStudent(
                            enrolment
                          )
                        }
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 12px', lineHeight: '1.5' },
  topHorizontalScroll: {
    position: 'fixed',
    zIndex: 190,
    overflowX: 'auto',
    overflowY: 'hidden',
    height: '18px',
    border:
      '1px solid #b8c5ce',
    borderRadius: '6px',
    background: 'white',
    boxSizing: 'border-box',
    boxShadow:
      '0 -2px 8px rgba(31, 41, 51, 0.12)',
    WebkitOverflowScrolling:
      'touch',
    overscrollBehaviorX:
      'contain'
  },

  topHorizontalScrollHidden: {
    display: 'none'
  },

  topHorizontalScrollTrack: {
    height: '1px'
  },

  tableWrap: {
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    minWidth: '760px'
  },
  th: {
    textAlign: 'left',
    padding: '9px 11px',
    fontSize: '13px',
    color: '#42515f',
    background: '#f0f4f7',
    borderBottom: '1px solid #d7dee5',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 2
  },
  thStudent: {
    width: '220px',
    position: 'sticky',
    left: 0,
    zIndex: 5,
    background: '#f0f4f7',
    boxShadow:
      '2px 0 4px rgba(31, 41, 51, 0.08)'
  },
  thAssignment: {
    width: '130px',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: '1.2'
  },
  thSummary: {
    width: '90px',
    textAlign: 'center'
  },
  thDetails: {
    width: '80px',
    textAlign: 'center'
  },
  tdName: {
    padding: '9px 11px',
    borderBottom: '1px solid #d7dee5',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    verticalAlign: 'middle',
    lineHeight: '1.25',
    position: 'sticky',
    left: 0,
    zIndex: 3,
    background: 'white',
    boxShadow:
      '2px 0 4px rgba(31, 41, 51, 0.08)'
  },
  studentPrimary: {
    color: '#1f2933',
    fontWeight: '700'
  },
  studentSecondary: {
    marginTop: '3px',
    color: '#66788a',
    fontSize: '11px',
    fontWeight: '500',
    lineHeight: '1.25'
  },
  td: { padding: '9px 11px', borderBottom: '1px solid #d7dee5', fontSize: '14px' },
  pill: {
    display: 'inline-block', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  viewBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 200, padding: '12px',
    overflowY: 'auto', overflowX: 'hidden',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch'
  },
  panel: {
    background: 'white', borderRadius: '12px', width: '100%',
    maxWidth: '800px', margin: '0 auto', display: 'flex',
    flexDirection: 'column', boxShadow: '0 18px 60px rgba(0,0,0,0.25)'
  },
  panelHeader: {
    position: 'sticky', top: 0, zIndex: 10,
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '18px 24px', borderBottom: '1px solid #d7dee5',
    background: 'white', borderRadius: '12px 12px 0 0', flexShrink: 0
  },
  panelTitle: { margin: '0 0 4px', fontSize: '18px', color: '#1f2933' },
  panelSub: { fontSize: '13px', color: '#66788a' },
  closeBtn: {
    border: 'none', background: 'none', fontSize: '28px',
    cursor: 'pointer', color: '#66788a', lineHeight: 1
  },
  panelBody: { padding: '24px', overflow: 'visible', flex: '0 0 auto' },
  section: { marginBottom: '28px' },
  sectionHeading: { fontSize: '16px', color: '#1f2933', margin: '0 0 12px' },
  attGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  attCell: {
    borderRadius: '8px', padding: '8px 10px', textAlign: 'center',
    fontSize: '12px', fontWeight: '700', minWidth: '48px'
  },
  attWeekNum: { fontSize: '11px', marginBottom: '2px' },
  attWeekDate: { fontSize: '10px', marginBottom: '4px', opacity: 0.7 },
  cqItem: {
    border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '10px 12px', marginBottom: '8px', background: 'white'
  },
  cqMeta: { fontSize: '12px', color: '#66788a', fontWeight: '700', marginBottom: '4px' },
  cqText: { fontSize: '14px', color: '#1f2933', lineHeight: '1.5' },
  pdItem: {
    border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '12px', marginBottom: '10px', background: 'white'
  },
  pdMeta: {
    fontSize: '12px', color: '#66788a', fontWeight: '700',
    marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px'
  },
  pdText: { fontSize: '14px', color: '#1f2933', lineHeight: '1.6', whiteSpace: 'pre-wrap' },
  gradedTag: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  feedbackBox: {
    background: '#f0f4f7', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '10px 12px', marginTop: '10px', fontSize: '14px', lineHeight: '1.5'
  },
  feedbackLabel: {
    fontSize: '11px', fontWeight: '700', color: '#66788a',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px'
  },
  assignmentItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 12px', border: '1px solid #d7dee5',
    borderRadius: '8px', marginBottom: '8px'
  },
  assignmentLabel: { fontSize: '14px', fontWeight: '600', color: '#1f2933' },
  completedTag: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  submittedTag: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  notCompletedTag: {
    background: '#f0f4f7', color: '#66788a', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  downloadLink: {
    display: 'inline-block', color: '#215b7d', fontSize: '13px',
    textDecoration: 'underline', marginTop: '6px'
  },
  finalGradeBox: {
    background: '#e7f5ed', borderRadius: '8px', padding: '10px 14px',
    fontSize: '15px', color: '#216e45', marginBottom: '8px'
  }
};
